import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { listPlans } from "../../api/catalog";
import { floorError, listPinBackedPlans, markPinBackedPlan, unmarkPinBackedPlan, type PinBackedPlan } from "../../api/pinBackedPlans";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Plans that use a pin (owner 2026-10-10, per plan): shown to an operator that
 * holds pins. Each recharge, new user or plan change on a marked plan uses one
 * pin that covers the speed and term below; with none left it is refused.
 */
export function PinBackedPlansSection({ ns, mandatory = false }: { ns: string; mandatory?: boolean }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const marked = useQuery({
    queryKey: ["pinbackedplans", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listPinBackedPlans(identity, ns, signal),
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["pinbackedplans", ns] });
  const unmark = useMutation({ mutationFn: (g: string) => unmarkPinBackedPlan(identity, ns, g), onSuccess: refresh });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">Plans that use a pin</Typography>
          <Typography variant="body2" color="text.secondary">
            {mandatory
              ? "Every recharge, new user or plan change is paid by a pin that covers the plan's own speed and term, or from your funds, in your pay order. Your plans need no mark. Mark a plan only to make it need a faster or longer pin than the plan itself."
              : "Every recharge, new user or plan change on these plans uses one of your pins. With no pin left it is refused — buy pins first. Your other plans run on your own bandwidth as before."}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setAdding(true)}>
          Mark a plan
        </Button>
      </Box>
      {marked.error && <Alert severity="error">{errText(marked.error)}</Alert>}
      {unmark.error && <Alert severity="error">{errText(unmark.error)}</Alert>}
      {marked.data && marked.data.length === 0 && (
        <Alert severity="info">
          {mandatory ? "No plan is marked: each plan uses the pin its own speed and term need." : "No plan uses a pin yet."}
        </Alert>
      )}
      {marked.data && marked.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell>A pin must cover</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {marked.data.map((p: PinBackedPlan) => (
                <TableRow key={p.groupname}>
                  <TableCell>{p.groupname}</TableCell>
                  <TableCell>
                    {p.minspeedmbps} Mbps, {p.mintermdays} days
                  </TableCell>
                  <TableCell align="right">
                    {!mandatory && (
                      <Button size="small" color="error" disabled={unmark.isPending} onClick={() => unmark.mutate(p.groupname)}>
                        Stop using pins
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {adding && <MarkDialog ns={ns} onClose={() => setAdding(false)} onDone={() => { refresh(); setAdding(false); }} />}
    </Box>
  );
}

function MarkDialog({ ns, onClose, onDone }: { ns: string; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const plans = useQuery({ queryKey: ["plans", ns], queryFn: async () => (await listPlans(identity, ns)).data ?? [], enabled: Boolean(ns) });
  const [groupname, setGroupname] = useState("");
  const [speed, setSpeed] = useState("");
  const [term, setTerm] = useState("");
  const speedErr = floorError(speed, "Speed", 100000);
  const termErr = floorError(term, "Term", 3660);
  const mark = useMutation({ mutationFn: () => markPinBackedPlan(identity, ns, groupname, Number(speed), Number(term)), onSuccess: onDone });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Make a plan use a pin</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {plans.error && <Alert severity="error">{errText(plans.error)}</Alert>}
          <TextField select label="Plan" value={groupname} onChange={(e) => setGroupname(e.target.value)} fullWidth>
            {(plans.data ?? []).map((p) => (
              <MenuItem key={p.groupname} value={p.groupname}>
                {p.group_hd || p.groupname}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Pin speed at least (Mbps)" value={speed} onChange={(e) => setSpeed(e.target.value)} error={speed !== "" && Boolean(speedErr)} helperText={speed !== "" ? speedErr ?? " " : "The plan's speed"} fullWidth />
          <TextField label="Pin term at least (days)" value={term} onChange={(e) => setTerm(e.target.value)} error={term !== "" && Boolean(termErr)} helperText={term !== "" ? termErr ?? " " : "The plan's validity"} fullWidth />
          <Alert severity="warning">From now on each recharge on this plan uses one pin, and is refused when none is left.</Alert>
          {mark.error && <Alert severity="error">{errText(mark.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!groupname || Boolean(speedErr) || Boolean(termErr) || mark.isPending} onClick={() => mark.mutate()}>
          Mark
        </Button>
      </DialogActions>
    </Dialog>
  );
}
